import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { CircleNotch, CheckCircle } from '@phosphor-icons/react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/* ───── Perguntas SUS (System Usability Scale) ───── */
const SUS_QUESTIONS = [
  'Eu gostaria de usar este sistema frequentemente.',
  'Achei o sistema desnecessariamente complexo.',
  'Achei o sistema facil de usar.',
  'Acho que precisaria de ajuda tecnica para usar este sistema.',
  'Achei que as funcoes do sistema estao bem integradas.',
  'Achei que havia muita inconsistencia no sistema.',
  'Imagino que a maioria das pessoas aprenderia a usar este sistema rapidamente.',
  'Achei o sistema muito complicado de usar.',
  'Me senti muito confiante usando o sistema.',
  'Precisei aprender muitas coisas antes de conseguir usar o sistema.',
] as const;

const SCALE_LABELS = [
  'Discordo totalmente',
  'Discordo',
  'Neutro',
  'Concordo',
  'Concordo totalmente',
] as const;

/* ───── Calculo do SUS Score ───── */
function calculate_sus_score(answers: number[]): number {
  let total = 0;
  for (let i = 0; i < 10; i++) {
    if (i % 2 === 0) {
      // impares (1,3,5,7,9): nota - 1
      total += answers[i] - 1;
    } else {
      // pares (2,4,6,8,10): 5 - nota
      total += 5 - answers[i];
    }
  }
  return total * 2.5;
}

interface SusSurveyModalProps {
  open: boolean;
  on_open_change: (open: boolean) => void;
  user_id: string;
  on_completed: () => void;
}

export function SusSurveyModal({ open, on_open_change, user_id, on_completed }: SusSurveyModalProps) {
  const [answers, set_answers] = useState<(number | null)[]>(Array(10).fill(null));
  const [current_step, set_current_step] = useState(0); // 0-9 perguntas, 10 = resultado
  const [submitting, set_submitting] = useState(false);
  const [final_score, set_final_score] = useState<number | null>(null);

  const all_answered = answers.every((a) => a !== null);
  const current_answered = answers[current_step] !== null;

  const handle_answer = (question_index: number, value: number) => {
    set_answers((prev) => {
      const next = [...prev];
      next[question_index] = value;
      return next;
    });
  };

  const handle_submit = async () => {
    if (!all_answered) return;
    set_submitting(true);

    const valid_answers = answers as number[];
    const score = calculate_sus_score(valid_answers);

    const row = {
      user_id,
      q1: valid_answers[0],
      q2: valid_answers[1],
      q3: valid_answers[2],
      q4: valid_answers[3],
      q5: valid_answers[4],
      q6: valid_answers[5],
      q7: valid_answers[6],
      q8: valid_answers[7],
      q9: valid_answers[8],
      q10: valid_answers[9],
      sus_score: score,
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase.from('sus_surveys') as any).insert(row);

    set_submitting(false);

    if (error) {
      toast.error('Erro ao salvar pesquisa.');
      return;
    }

    set_final_score(score);
    set_current_step(10);
    on_completed();
  };

  const handle_close = () => {
    on_open_change(false);
    // reset apos fechar
    setTimeout(() => {
      set_answers(Array(10).fill(null));
      set_current_step(0);
      set_final_score(null);
    }, 300);
  };

  const is_thank_you = current_step === 10;

  return (
    <Dialog open={open} onOpenChange={handle_close}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <AnimatePresence mode="wait">
          {is_thank_you ? (
            <motion.div
              key="thanks"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center py-8 text-center gap-4"
            >
              <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-primary" weight="fill" />
              </div>
              <DialogHeader className="items-center">
                <DialogTitle className="text-xl">Obrigado pelo feedback!</DialogTitle>
                <DialogDescription className="mt-2">
                  Sua avaliacao nos ajuda a melhorar a plataforma.
                </DialogDescription>
              </DialogHeader>
              <Button onClick={handle_close} className="mt-2 rounded-lg">
                Fechar
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key={`q-${current_step}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <DialogHeader>
                <DialogTitle className="text-base font-semibold">
                  Pesquisa de Experiencia
                </DialogTitle>
                <DialogDescription className="text-xs">
                  Pergunta {current_step + 1} de 10
                </DialogDescription>
              </DialogHeader>

              {/* barra de progresso */}
              <div className="h-1 rounded-full bg-muted mt-3 mb-5 overflow-hidden">
                <motion.div
                  className="h-full bg-primary rounded-full"
                  initial={false}
                  animate={{ width: `${((current_step + (current_answered ? 1 : 0)) / 10) * 100}%` }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              </div>

              <p className="text-sm text-foreground font-medium mb-4 leading-relaxed">
                {SUS_QUESTIONS[current_step]}
              </p>

              <RadioGroup
                value={answers[current_step]?.toString() ?? ''}
                onValueChange={(v) => handle_answer(current_step, Number(v))}
                className="space-y-2"
              >
                {SCALE_LABELS.map((label, i) => {
                  const value = i + 1;
                  const is_selected = answers[current_step] === value;
                  return (
                    <Label
                      key={value}
                      htmlFor={`q${current_step}-${value}`}
                      className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                        is_selected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-accent/50'
                      }`}
                    >
                      <RadioGroupItem value={value.toString()} id={`q${current_step}-${value}`} />
                      <span className="text-sm flex-1">{label}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">{value}</span>
                    </Label>
                  );
                })}
              </RadioGroup>

              <div className="flex items-center justify-between mt-6 gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={current_step === 0}
                  onClick={() => set_current_step((s) => s - 1)}
                  className="text-xs"
                >
                  Anterior
                </Button>

                {current_step < 9 ? (
                  <Button
                    size="sm"
                    disabled={!current_answered}
                    onClick={() => set_current_step((s) => s + 1)}
                    className="text-xs rounded-lg"
                  >
                    Proxima
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    disabled={!all_answered || submitting}
                    onClick={handle_submit}
                    className="text-xs rounded-lg"
                  >
                    {submitting ? (
                      <><CircleNotch className="h-3.5 w-3.5 mr-1.5 animate-spin" />Enviando...</>
                    ) : (
                      'Enviar'
                    )}
                  </Button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
